import React, { useState, useRef } from 'react';
import {
  Flex, Text, Box, ActionNote, StateTaxFormInfo,
} from '../../components';
import { STEPS } from '../commonStateTaxData';
import {
  step1InitialQuestions, maritalStatusQuestion, maritalStatusAllowancesOptions, deductionQuestion,
  worksheetDeductionQuestions, worksheetCommonDeductionQuestions, allowancesQuestionsPartTwo,
  exemptionInitialQuestions, civilReliefActStateQuestion,
} from './data';
import FormConsumer from '../../collect-info/FormConsumers';
import CollapsibleQuestions from '../CollapsibleQuestions';
import * as S from '../../styles';
import StateTaxFormLayout from '../common/state-tax-content-layout';

function Version521({
  prefilledData = {},
  saveFormData,
  isSubmitting,
  scrollTop,
  handleSigning,
  viewResultAsManager,
  data,
  getPdfUrl,
  getInitialQuestions,
  resetInitialQuestions,
  formLink,
  isOnboardingV2,
}) {
  const [currentStep, setCurrentStep] = useState(STEPS.step1);

  const formatFullName = () => {
    const firstName = `${`${prefilledData.employee_first_name} `}` || '';
    const middleInitial = prefilledData.employee_middle_name;
    const lastName = prefilledData.employee_last_name || '';
    return `${firstName}${middleInitial ? `${middleInitial} ` : ''}${lastName}`;
  };
  prefilledData.employee_name = formatFullName();

  // Q1AB, Q2AB:
  const basicInfoForm = useRef();
  const step1InitQ = getInitialQuestions(step1InitialQuestions);
  const [basicInfo, setBasicInfo] = useState(step1InitQ);

  // Q3-1:
  const maritalStatusForm = useRef();
  const maritalStatusInitQ = getInitialQuestions(maritalStatusQuestion);
  const [maritalStatus, setMaritalStatus] = useState(maritalStatusInitQ);

  // Q3-2: num allowances claimed for marital status
  const getMaritalStatusNumAllowances = (questions) => {
    if (questions[0].answer.answer.filing_status !== '' || data.filing_status) {
      const fs = questions[0].answer.answer.filing_status || data.filing_status;
      const maritalStatusAllowanceQuestions = maritalStatusAllowancesOptions[fs];
      const maritalStatusAllowancesInitQ = getInitialQuestions(maritalStatusAllowanceQuestions);
      return maritalStatusAllowancesInitQ;
    }
  };
  const [maritalStatusNumAllowances, setMaritalStatusNumAllowances] = useState(data.filing_status ? getMaritalStatusNumAllowances(maritalStatus) : []);
  // const [maritalStatusNumAllowancesError, setMaritalStatusNumAllowancesError] = useState(false);

  const [isExemptFromWithholding, setIsExemptFromWithholding] = useState(data.is_exempt || '');

  const [numDependentAllowances, setNumDependentAllowances] = useState(data.number_of_dependent_allowances || '');

  // WS inital questions (-> claiming exemptions? which ones?)
  const [worksheetOpen, setWorksheetOpen] = useState(false);
  const worksheetForm = useRef();

  // WS inital questions (-> claiming exemptions? which ones?)
  const claimDeductionInitQ = getInitialQuestions(deductionQuestion);
  const [claimDeduction, setClaimDeduction] = useState(claimDeductionInitQ);

  // WS deduction specific questions:
  const getWSDeductionSpecificQuestions = (questions) => {
    if (questions[0].answer.answer.deduction !== '' || data.deduction) {
      const d = questions[0].answer.answer.deduction || data.deduction;
      const deductionQuestions = worksheetDeductionQuestions[d];
      const deductionInitQ = getInitialQuestions(deductionQuestions);
      return deductionInitQ;
    }
  };
  const [wsDeductionSpecificQuestions, setWsDeductionSpecificQuestions] = useState(data.deduction ? getWSDeductionSpecificQuestions(claimDeduction) : []);

  // WS common deduction questions:
  const wsCommonDeductionInitQ = getInitialQuestions(worksheetCommonDeductionQuestions);
  const [wsCommonDeductions, setWsCommonDeductions] = useState(wsCommonDeductionInitQ);

  // Q5:
  const allowancesForm = useRef();
  const allowancesPtTwoInitQ = getInitialQuestions(allowancesQuestionsPartTwo);
  const [additionalAllowances, setAdditionalAllowances] = useState(allowancesPtTwoInitQ);

  // Q8:
  const exemptionsForm = useRef();
  const exemptionInitQ = getInitialQuestions(exemptionInitialQuestions);
  const [claimExemption, setClaimExemption] = useState(exemptionInitQ);

  const getExemptionSpecificQuestions = (questions) => {
    if (questions[0].answer.answer.exemption_from_withholding === 'civil_relief_act' || data.exemption_from_withholding === 'civil_relief_act') {
      const exemptionSpecificiInitQ = getInitialQuestions(civilReliefActStateQuestion);
      return exemptionSpecificiInitQ;
    }
  };
  const [exemptionSpecificQuestions, setExemptionSpecificQuestions] = useState(data.exemption_from_withholding ? getExemptionSpecificQuestions(claimExemption) : []);
  const [exemptionSpecificQuestionsError, setExemptionSpecificQuestionsError] = useState(false);

  const renderBasicInfoForm = (isReviewStep) => (
    <div>
      <FormConsumer
        questions={basicInfo}
        {...(isReviewStep && { viewResultAsManager: true, reviewActions: [] })}
        onQuestionChange={({ question, value }) => {
          const questions = basicInfo.map((q) => {
            if (q.id !== question.id) return q;
            const answer = {
              ...q.answer,
              answer: {
                ...q.answer.answer,
                [q.id]: value,
              },
            };
            const updatedQuestion = { ...q, answer };
            return updatedQuestion;
          });
          setBasicInfo(questions);
        }}
        ref={basicInfoForm}
      />
    </div>
  );

  const renderMaritalStatusNumAllowances = (isReviewStep) => (
    <FormConsumer
      questions={maritalStatusNumAllowances}
      {...(isReviewStep && { viewResultAsManager: true, reviewActions: [] })}
      onQuestionChange={({ question, value }) => {
        const questions = maritalStatusNumAllowances.map((q) => {
          if (q.id !== question.id) return q;
          const answer = {
            ...q.answer,
            answer: {
              ...q.answer.answer,
              [q.id]: value,
            },
          };
          const updatedQuestion = { ...q, answer };
          return updatedQuestion;
        });
        setMaritalStatusNumAllowances(questions);
      }}
      ref={maritalStatusForm}
    />
  );

  const renderMaritalStatusForm = (isReviewStep) => (
    <Flex column>
      <FormConsumer
        questions={maritalStatus}
        {...(isReviewStep && { viewResultAsManager: true, reviewActions: [] })}
        onQuestionChange={({ question, value }) => {
          const questions = maritalStatus.map((q) => {
            if (q.id !== question.id) return q;
            const answer = {
              ...q.answer,
              answer: {
                ...q.answer.answer,
                [q.id]: value,
              },
            };
            const updatedQuestion = { ...q, answer };
            return updatedQuestion;
          });
          setMaritalStatus(questions);
          setMaritalStatusNumAllowances(getMaritalStatusNumAllowances(questions));
        }}
        ref={maritalStatusForm}
      />
      { maritalStatus[0].answer.answer.filing_status !== '' ? renderMaritalStatusNumAllowances(isReviewStep) : ''}
      <Flex column>
        <S.QuestionLabel mb="7px" isRequired>
          I wish to be exempt from withholding.
        </S.QuestionLabel>
        <S.QuestionWrapper mb="20px">
          <label>
            <Flex alignItems="center">
              <S.Input
                type="radio"
                value={isExemptFromWithholding}
                checked={isExemptFromWithholding}
                disabled={isReviewStep}
                width="auto"
                onChange={(e) => {
                  setIsExemptFromWithholding(!isExemptFromWithholding);
                }}
              />
              <Box ml={2} />
              Yes
            </Flex>
          </label>
          <label>
            <Flex alignItems="center">
              <S.Input
                type="radio"
                value={!isExemptFromWithholding}
                checked={!isExemptFromWithholding}
                disabled={isReviewStep}
                width="auto"
                onChange={(e) => {
                  setIsExemptFromWithholding(!isExemptFromWithholding);
                }}
              />
              <Box ml={2} />
              No
            </Flex>
          </label>
        </S.QuestionWrapper>
      </Flex>
    </Flex>
  );

  const renderWsDeductionSpecificQuestions = (isReviewStep) => (
    <Flex column>
      <FormConsumer
        questions={wsDeductionSpecificQuestions}
        {...(isReviewStep && { viewResultAsManager: true, reviewActions: [] })}
        onQuestionChange={({ question, value }) => {
          const questions = wsDeductionSpecificQuestions.map((q) => {
            if (q.id !== question.id) return q;
            const answer = {
              ...q.answer,
              answer: {
                ...q.answer.answer,
                [q.id]: value,
              },
            };
            const updatedQuestion = { ...q, answer };
            return updatedQuestion;
          });
          setWsDeductionSpecificQuestions(questions);
        }}
        ref={exemptionsForm}
      />
    </Flex>
  );

  const renderWorksheetContent = (isReviewStep) => (
    <Flex column>
      <FormConsumer
        questions={claimDeduction}
        {...(isReviewStep && { viewResultAsManager: true, reviewActions: [] })}
        onQuestionChange={({ question, value }) => {
          const questions = claimDeduction.map((q) => {
            if (q.id !== question.id) return q;
            const answer = {
              ...q.answer,
              answer: {
                ...q.answer.answer,
                [q.id]: value,
              },
            };
            const updatedQuestion = { ...q, answer };
            return updatedQuestion;
          });
          setClaimDeduction(questions);
          setWsDeductionSpecificQuestions(getWSDeductionSpecificQuestions(questions));
        }}
        ref={worksheetForm}
      />
      { claimDeduction[0].answer.answer.deduction !== '' ? renderWsDeductionSpecificQuestions(isReviewStep) : '' }
      <FormConsumer
        questions={wsCommonDeductions}
        {...(isReviewStep && { viewResultAsManager: true, reviewActions: [] })}
        onQuestionChange={({ question, value }) => {
          const questions = wsCommonDeductions.map((q) => {
            if (q.id !== question.id) return q;
            const answer = {
              ...q.answer,
              answer: {
                ...q.answer.answer,
                [q.id]: value,
              },
            };
            const updatedQuestion = { ...q, answer };
            return updatedQuestion;
          });
          setWsCommonDeductions(questions);
        }}
        ref={worksheetForm}
      />
      <Flex column>
        <Flex mb="7px">
          <S.QuestionLabel>
            Total Additional Allowances
          </S.QuestionLabel>
        </Flex>
        <S.QuestionWrapper>
          <Flex mb="10px">
            <S.Input
              type="text"
              value={(worksheetOpen && maritalStatusNumAllowances.length != 0) ? calculateTotalAdditionalAllowances() : totalAdditionalAllowances}
              onChange={(e) => setTotalAdditionalAllowances(e.target.value)}
              disabled
            />
          </Flex>
        </S.QuestionWrapper>
        {totalAdditionalAllowancesError && (
          <Flex justifyContent="flex-start">
            <Text color="red">Please enter a valid response.</Text>
          </Flex>
        )}
      </Flex>
    </Flex>
  );

  // calculations here!
  const [numAdditionalAllowances, setNumAdditionalAllowances] = useState(data.additional_allowances || '');
  const [numAdditionalAllowancesError, setNumAdditionalAllowancesError] = useState(false);

  const [totalAdditionalAllowances, setTotalAdditionalAllowances] = useState(numAdditionalAllowances);
  const [totalAdditionalAllowancesError, setTotalAdditionalAllowancesError] = useState(false);

  const calculateNumAdditionalAllowances = () => {
    let line1 = 0;
    let lineC = 0;

    if (claimDeduction[0].answer.answer.deduction === 'georgia_standard_deduction') {
      const yourself = (wsDeductionSpecificQuestions[0].answer.answer.self_age_blindness).length || 0;
      const spouse = (wsDeductionSpecificQuestions[1].answer.answer.spouse_age_blindness).length || 0;
      const numBoxesChecked = yourself + spouse;

      line1 = numBoxesChecked * 1300;
    } else if (claimDeduction[0].answer.answer.deduction === 'itemized_deduction') {
      lineC = wsDeductionSpecificQuestions[0].answer.answer.itemized_deduction_value || 0;
    }

    const lineD = wsCommonDeductions[0].answer.answer.allowable_deductions_to_federal_adjusted_gross_income || 0;
    const lineE = parseInt(line1) + parseInt(lineC) + parseInt(lineD);

    const lineF = wsCommonDeductions[1].answer.answer.estimate_of_taxable_income || 0;

    // if lineG is 0 or less, stop here:
    const lineG = lineE - parseInt(lineF);

    // else, if lineG is greater than 0:
    if (lineG > 0) {
      const lineH = lineG / 3000;
      const lineHRemainder = lineG % 3000;
      if (lineHRemainder > 1500) {
        return Math.ceil(lineH);
      }
      return Math.round(lineH);
    }
    if (lineG < 0) {
      return 0;
    }

    return lineG;
  };

  const calculateTotalAdditionalAllowances = () => {
    if (maritalStatusNumAllowances.length != 0) {
      const q3 = maritalStatusNumAllowances[0].answer.answer.total_number_of_allowances || 0;
      const q4 = numDependentAllowances || 0;
      const q5 = calculateNumAdditionalAllowances() || 0;

      const q3Val = parseInt(q3);
      const q4Val = parseInt(q4);
      return q3Val + q4Val + q5;
    }

    return calculateNumAdditionalAllowances() || 0;
  };

  const renderWorksheet = (isReviewStep) => (
    <Flex column>
      <Flex column>
        <Flex mb="7px">
          <S.QuestionLabel>
            Number of dependent allowances
          </S.QuestionLabel>
        </Flex>
        <S.QuestionWrapper>
          <Flex mb="10px">
            <S.Input
              type="text"
              value={numDependentAllowances}
              onChange={(e) => setNumDependentAllowances(e.target.value)}
              disabled={isReviewStep}
            />
          </Flex>
        </S.QuestionWrapper>
      </Flex>
      <Flex column>
        <Flex mb="7px">
          <S.QuestionLabel>
            Number of additional allowances
          </S.QuestionLabel>
        </Flex>
        <S.QuestionWrapper>
          <Flex mb="10px">
            <S.Input
              type="text"
              value={worksheetOpen ? calculateNumAdditionalAllowances() : numAdditionalAllowances}
              onChange={(e) => setNumAdditionalAllowances(e.target.value)}
              disabled
            />
          </Flex>
        </S.QuestionWrapper>
        {numAdditionalAllowancesError && (
          <Flex justifyContent="flex-start">
            <Text color="red">Please enter a valid response.</Text>
          </Flex>
        )}
      </Flex>
      <Flex mt="10px">
        <CollapsibleQuestions
          title="To claim additional allowances, you must complete the worksheet to calculate an amount."
          handleClick={() => {
            if (!isReviewStep) {
              setWorksheetOpen(!worksheetOpen);
            }
          }}
          open={worksheetOpen || isReviewStep}
        >
          {renderWorksheetContent(isReviewStep)}
        </CollapsibleQuestions>
      </Flex>
    </Flex>
  );

  const renderAllowancesForm = (isReviewStep) => (
    <Flex>
      <FormConsumer
        questions={additionalAllowances}
        {...(isReviewStep && { viewResultAsManager: true, reviewActions: [] })}
        onQuestionChange={({ question, value }) => {
          const questions = additionalAllowances.map((q) => {
            if (q.id !== question.id) return q;
            const answer = {
              ...q.answer,
              answer: {
                ...q.answer.answer,
                [q.id]: value,
              },
            };
            const updatedQuestion = { ...q, answer };
            return updatedQuestion;
          });
          setAdditionalAllowances(questions);
        }}
        ref={allowancesForm}
      />
    </Flex>
  );

  const renderExemptionSpecificQuestions = (isReviewStep) => (
    <Flex column>
      <FormConsumer
        questions={exemptionSpecificQuestions}
        {...(isReviewStep && { viewResultAsManager: true, reviewActions: [] })}
        onQuestionChange={({ question, value }) => {
          const questions = exemptionSpecificQuestions.map((q) => {
            if (q.id !== question.id) return q;
            const answer = {
              ...q.answer,
              answer: {
                ...q.answer.answer,
                [q.id]: value,
              },
            };
            const updatedQuestion = { ...q, answer };
            return updatedQuestion;
          });
          setExemptionSpecificQuestions(questions);
        }}
        ref={exemptionsForm}
      />
    </Flex>
  );

  const renderExemptionsForm = (isReviewStep) => (
    <Flex column>
      <FormConsumer
        questions={claimExemption}
        {...(isReviewStep && { viewResultAsManager: true, reviewActions: [] })}
        onQuestionChange={({ question, value }) => {
          const questions = claimExemption.map((q) => {
            if (q.id !== question.id) return q;
            const answer = {
              ...q.answer,
              answer: {
                ...q.answer.answer,
                [q.id]: value,
              },
            };
            const updatedQuestion = { ...q, answer };
            return updatedQuestion;
          });
          setClaimExemption(questions);
          setExemptionSpecificQuestions(getExemptionSpecificQuestions(questions));
        }}
        ref={exemptionsForm}
      />
      { exemptionSpecificQuestionsError && isExemptFromWithholding && (
        <Flex justifyContent="flex-start">
          <Text color="red">Your state of residence and your spouse's must be the same to be exempt.</Text>
        </Flex>
      )}
      { claimExemption[0].answer.answer.exemption_from_withholding === 'civil_relief_act' ? renderExemptionSpecificQuestions(isReviewStep) : ''}
    </Flex>
  );

  const renderStep1 = () => {
    let disabled;
    let disabledText;
    if (viewResultAsManager) {
      disabled = true;
      disabledText = 'This step has already been completed.';
    } else if (companyStaff && companyStaff.id) {
      disabled = true;
      disabledText = 'This step should be completed by the employee.';
    }

    return (
      <Flex column>
        {
          isOnboardingV2 ? null : (
            <Text fontSize="22px" bold>
              Fill state tax form
            </Text>
          )
        }
        <a href={formLink} target="_blank" rel="noreferrer">
          View original form and instructions
        </a>
        <Flex column mt={16}>
          {renderBasicInfoForm()}
          {renderMaritalStatusForm()}
          {isExemptFromWithholding && renderExemptionsForm()}
          {!isExemptFromWithholding && renderWorksheet()}
          {!isExemptFromWithholding && renderAllowancesForm()}
        </Flex>
        {viewResultAsManager && companyStaff.pii_permission === 'full_access' && (
          <Flex column mt={10} mb={10}>
            <Flex alignItems="center">
              <Text mr={16}>Download signed documents</Text>
              <a href={getPdfUrl()} target="_blank" rel="noopener noreferrer">
                <img src="/j/images/onboarding/download.svg" />
              </a>
            </Flex>
          </Flex>
        )}
        <S.Button
          disabled={disabled}
          onClick={() => {
            let err = null;
            if (basicInfoForm && basicInfoForm.current) {
              basicInfoForm.current.validate((error) => {
                if (error) err = error;
              });
            }
            if (maritalStatusForm && maritalStatusForm.current) {
              maritalStatusForm.current.validate((error) => {
                if (error) err = error;
              });
            }
            if (exemptionsForm && exemptionsForm.current) {
              exemptionsForm.current.validate((error) => {
                if (error) err = error;
              });
            }
            if (worksheetForm && worksheetForm.current) {
              worksheetForm.current.validate((error) => {
                if (error) err = error;
              });
            }
            if (allowancesForm && allowancesForm.current) {
              allowancesForm.current.validate((error) => {
                if (error) err = error;
              });
            }
            if (isExemptFromWithholding) {
              if (claimExemption[0].answer.answer.exemption_from_withholding === 'civil_relief_act') {
                if (exemptionSpecificQuestions[0].answer.answer.state_of_residence !== '' && (basicInfo[4].answer.answer.state !== exemptionSpecificQuestions[0].answer.answer.state_of_residence)) {
                  err = true;
                  setExemptionSpecificQuestionsError(true);
                } else {
                  setExemptionSpecificQuestionsError(false);
                }
              }
            }
            if (!(/^\d*$/.test(numAdditionalAllowances))) {
              err = true;
              setNumAdditionalAllowancesError(true);
            } else {
              setNumAdditionalAllowancesError(false);
            }
            if (!(/^\d*$/.test(totalAdditionalAllowances))) {
              err = true;
              setTotalAdditionalAllowancesError(true);
            } else {
              setTotalAdditionalAllowancesError(false);
            }
            if (err) {

            } else {
              // No error, proceed to save
              const basicInfoData = basicInfo.reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});
              const maritalStatusData = maritalStatus.reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});
              const maritalStatusNumAllowancesData = maritalStatusNumAllowances.reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});

              let deductionData;
              let worksheetDeductionData;
              let worksheetCommonDeductionData;
              let worksheetResetDeductionData;
              let additionalAllowancesData;
              let claimExemptionData;
              let exemptionStateData;

              let numAdditionalAllowancesTemp;
              let totalAdditionalAllowancesTemp;
              let numDependentAllowancesTemp;

              // if exempt from withholding
              if (isExemptFromWithholding) {
                // save answers to exemption questions
                claimExemptionData = claimExemption.reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});

                if (claimExemptionData.exemption_from_withholding === 'civil_relief_act') {
                  exemptionStateData = exemptionSpecificQuestions.reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});
                } else {
                  exemptionStateData = resetInitialQuestions(civilReliefActStateQuestion).reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});
                }

                // reset all other non-exemption questions
                const worksheetItemizedDeductionData = resetInitialQuestions(worksheetDeductionQuestions.itemized_deduction).reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});
                const worksheetStandardDeductionData = resetInitialQuestions(worksheetDeductionQuestions.georgia_standard_deduction).reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});
                worksheetDeductionData = { ...worksheetStandardDeductionData, worksheetItemizedDeductionData };

                deductionData = resetInitialQuestions(deductionQuestion).reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});
                worksheetCommonDeductionData = resetInitialQuestions(worksheetCommonDeductionQuestions).reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});
                additionalAllowancesData = resetInitialQuestions(allowancesQuestionsPartTwo).reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});

                numAdditionalAllowancesTemp = '0';
                totalAdditionalAllowancesTemp = '0';
                numDependentAllowancesTemp = 0;
              } else {
                // save answers to non-exemption questions
                additionalAllowancesData = additionalAllowances.reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});

                // reset exemption questions
                claimExemptionData = resetInitialQuestions(exemptionInitialQuestions).reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});
                exemptionStateData = resetInitialQuestions(civilReliefActStateQuestion).reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});

                if (numDependentAllowances !== '') numDependentAllowancesTemp = numDependentAllowances;
                if (numAdditionalAllowances !== '') numAdditionalAllowancesTemp = numAdditionalAllowances;
                if (totalAdditionalAllowances !== '') totalAdditionalAllowancesTemp = totalAdditionalAllowances;
                numAdditionalAllowancesTemp = calculateNumAdditionalAllowances().toString();
                if (worksheetOpen) {
                  // save answers to worksheet questions
                  totalAdditionalAllowancesTemp = calculateTotalAdditionalAllowances().toString();
                  deductionData = claimDeduction.reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});

                  worksheetDeductionData = wsDeductionSpecificQuestions.reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});

                  const deductionType = claimDeduction[0].answer.answer.deduction;
                  if (deductionType === 'itemized_deduction') {
                    worksheetResetDeductionData = resetInitialQuestions(worksheetDeductionQuestions.georgia_standard_deduction).reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});
                  } else if (deductionType === 'georgia_standard_deduction') {
                    worksheetResetDeductionData = resetInitialQuestions(worksheetDeductionQuestions.itemized_deduction).reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});
                  }

                  worksheetCommonDeductionData = wsCommonDeductions.reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});
                } else {
                  deductionData = claimDeductionInitQ.reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});
                  worksheetCommonDeductionData = wsCommonDeductionInitQ.reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});
                }
              }

              const exemptConditionData = { is_exempt: isExemptFromWithholding };
              const allExemptionsData = { ...exemptConditionData, ...claimExemptionData, ...exemptionStateData };
              const allDeductionsData = {
                ...deductionData, ...worksheetDeductionData, ...worksheetCommonDeductionData, ...worksheetResetDeductionData,
              };
              const calculatedAllowancesData = { additional_allowances: numAdditionalAllowancesTemp, total_additional_allowances: totalAdditionalAllowancesTemp, number_of_dependent_allowances: numDependentAllowancesTemp };
              const allAllowancesData = { ...additionalAllowancesData, ...calculatedAllowancesData };
              const data = {
                ...basicInfoData, ...maritalStatusData, ...maritalStatusNumAllowancesData, ...allExemptionsData, ...allAllowancesData, ...allDeductionsData,
              };
              saveFormData(data, () => {
                setCurrentStep(STEPS.step2);
              });
              scrollTop();
            }
          }}
          loading={isSubmitting}
        >
          Next
        </S.Button>
        {disabledText && (
          <Flex justifyContent="flex-end">
            <ActionNote text={disabledText} />
          </Flex>
        )}
      </Flex>
    );
  };

  // show step 2: review form with filled in information
  const renderStep2 = () => (
    <Flex column>
      <Text fontSize="22px" bold>
        Review
      </Text>
      <StateTaxFormInfo />
      <a href={formLink} target="_blank" rel="noreferrer">
        View original form and instructions
      </a>
      <Flex column mt={16}>
        {renderBasicInfoForm(true)}
        {renderMaritalStatusForm(true)}
        {isExemptFromWithholding && renderExemptionsForm(true)}
        {!isExemptFromWithholding && renderWorksheet(true)}
        {!isExemptFromWithholding && renderAllowancesForm(true)}
      </Flex>
      <Flex mt="10px">
        <S.SecondaryButton
          disabled={viewResultAsManager}
          onClick={() => {
            setCurrentStep(STEPS.step1);
            scrollTop();
          }}
        >
          Back
        </S.SecondaryButton>
        <Flex flex={1} column ml={2}>
          <S.Button
            disabled={viewResultAsManager}
            onClick={() => {
              handleSigning();
              setCurrentStep(STEPS.step3);
              scrollTop();
            }}
            loading={isSubmitting}
          >
            Finalize and sign
          </S.Button>
        </Flex>
      </Flex>
    </Flex>
  );

  // show step 3: hellosign
  const renderStep3 = () => (
    <Flex column>
      <Text fontSize="22px" bold>
        Finalize and sign
      </Text>
      <Flex column mt={44}>
        <div id="iframe-container" style={{ height: '640px' }} />
      </Flex>
    </Flex>
  );
  const isSigningStep = currentStep === STEPS.step3;

  return (
    <StateTaxFormLayout isOnboardingV2={isOnboardingV2} isSigningStep={isSigningStep}>
      {currentStep === STEPS.step1 && renderStep1()}
      {currentStep === STEPS.step2 && renderStep2()}
      {currentStep === STEPS.step3 && renderStep3()}
    </StateTaxFormLayout>
  );
}

export default Version521;
